import { Box, Button, Flex, Grid, Skeleton } from '@radix-ui/themes';
import { BuildPriorityCallout } from 'components/common/build-priority-callout';
import { ErrorBoundary } from 'components/common/error-boundary';
import { NoDataPlaceholder } from 'components/common/table/no-data-placeholder';
import { GameDataGameCard } from 'components/sections/game-data/games/card';
import { GameDataContext } from 'contexts/game-data.context';
import { lightFormat } from 'date-fns';
import { t } from 'i18next';
import { ITableNoData } from 'interfaces/tables/no-data';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo, useState } from 'react';
import { useCookiesStore } from 'stores/use-cookies-store';
import { GameDataGameFilters } from './filters';

const ITEMS_PER_PAGE = 100;

export const GameDataGames = () => {
  const app = useCookiesStore((s) => s.app);

  const [pages, setPages] = useState(1);

  const { loading, seasonGames, filterGames } = useContext(GameDataContext);

  const games = useMemo(() => {
    return seasonGames

      .filter(
        (g) =>
          filterGames.teamPk === undefined ||
          [g.home.teamPk, g.away.teamPk].includes(filterGames.teamPk)
      )
      .filter(
        (g) =>
          filterGames.date === undefined ||
          g.officialDate.startsWith(
            // match YYYY-MM-DD format
            lightFormat(filterGames.date, LOCAL_DATE_FORMAT)
          )
      );
  }, [seasonGames, filterGames]);

  const noDataProps: ITableNoData = {
    noDataHeader: 'No Games to Display',
    noDataBody: 'No games match your search',
  };

  return (
    <ErrorBoundary identifier="GameDataGames">
      <Flex direction="column" gap="3">
        <GameDataGameFilters />

        <Box>
          <BuildPriorityCallout
            priority={app.game_data_import_options.priority}
          />
        </Box>

        <Skeleton loading={loading}>
          {games.length === 0 && <NoDataPlaceholder {...noDataProps} />}

          <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="3">
            {games
              .filter((_, i) => i < pages * ITEMS_PER_PAGE)
              .map((g) => (
                <GameDataGameCard key={g.gamePk} game={g} />
              ))}

            {games.length > pages * ITEMS_PER_PAGE && (
              <Flex
                justify="center"
                gridColumn={{ initial: undefined, md: 'span 2', lg: 'span 3' }}
              >
                <Button
                  onClick={() => setPages(pages + 1)}
                  variant={RADIX.BUTTON.VARIANT.SECONDARY}
                >
                  {t('common.load-more')}
                </Button>
              </Flex>
            )}
          </Grid>
        </Skeleton>
      </Flex>
    </ErrorBoundary>
  );
};
